import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ContentArticleHeader as ContentArticleHeaderSchema } from 'graphql-ts-types';
import type { DisplayProps, MaxWidthProps } from 'styled-system';
import { display, maxWidth, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Link } from 'common/components/Link';
import { BodyBoldBB1, BodyBoldBB2, HeadlineH2 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { Color } from 'common/theme/colors/types';
import type { TextTransformProps } from 'common/theme/system-utilities';
import { textTransform } from 'common/theme/system-utilities';
import { localizeDate } from 'common/utils/date';

const AUTHOR_ITEM_SEPARATOR = ', ';
const AUTHOR_LIST_SUFFIX = ' \u2022 ';

type ArticleAuthor = NonNullable<ContentArticleHeaderSchema['author_v2'][number]>;

const ArticleHeaderContainer = styled(Flex)<any>`
  ${space};
`;

const articleHeaderContainerStyles: ComponentProps<typeof ArticleHeaderContainer> = {
  flexDirection: 'column',
  m: '0 auto',
  alignItems: 'center',
  textAlign: 'center',
};

const ArticleCategory = styled(BodyBoldBB1)<DisplayProps & TextTransformProps>`
  ${display}
  ${textTransform}
`;

const articleCategoryStyles: ComponentProps<typeof ArticleCategory> = {
  as: 'span',
  color: 'sys.neutral.border.strong',
  display: 'inline-block',
  mb: ['sp_2', 'sp_4', null, 'sp_6'],
  textTransform: 'uppercase',
};

const ArticleTitle = styled(HeadlineH2)<MaxWidthProps>`
  ${maxWidth};
`;

const articleTitleStyles: ComponentProps<typeof ArticleTitle> = {
  as: 'h1',
  maxWidth: '100%',
  mt: 'sp_0',
};

interface ContentArticleHeaderProps {
  author?: string;
  publishingDate?: string;
  category?: Nullable<string>;
  isHeaderLeftAligned: boolean;
  isHeaderWithReducedWidth: boolean;
  isCategoryHidden: boolean;
  isArticleInfoHidden: boolean;
  articleTitle: string;
  articleTitleColor?: Nullable<string>;
  readingTimeMinutes: number;
  authorV2?: ContentArticleHeaderSchema['author_v2'];
}

const isAuthorWithName = (
  author: ContentArticleHeaderSchema['author_v2'][number]
): author is ArticleAuthor => Boolean(author?.name);

const ContentArticleHeader = ({
  publishingDate,
  category,
  articleTitle,
  articleTitleColor,
  isArticleInfoHidden,
  isCategoryHidden,
  isHeaderWithReducedWidth,
  isHeaderLeftAligned,
  readingTimeMinutes,
  authorV2 = [],
}: ContentArticleHeaderProps) => {
  const { t, locale } = useLocalization();
  const articlePublishingDate = publishingDate ? localizeDate(publishingDate, locale) : null;
  const validAuthors = authorV2.filter(isAuthorWithName);
  const authors = validAuthors.flatMap((currentAuthor, index) => {
    const authorUrl = currentAuthor.url?.fullUrl;
    const authorNode = authorUrl ? (
      <Link
        key={`${currentAuthor.name}-${authorUrl}`}
        href={authorUrl}
        aria-label={t('h24_more_info')}
      >
        {currentAuthor.name}
      </Link>
    ) : (
      <span key={`${currentAuthor.name}-${index}`}>{currentAuthor.name}</span>
    );

    return [
      authorNode,
      index < validAuthors.length - 1 ? AUTHOR_ITEM_SEPARATOR : AUTHOR_LIST_SUFFIX,
    ];
  });

  return (
    <ArticleHeaderContainer
      data-corgi-id="content-article-header"
      {...articleHeaderContainerStyles}
      {...(isHeaderLeftAligned
        ? {
            alignItems: 'flex-start',
            m: 0,
            textAlign: 'left',
          }
        : {})}
      {...(isHeaderWithReducedWidth ? { width: [1, 6 / 8, 8 / 12] } : {})}
    >
      {category && !isCategoryHidden && (
        <ArticleCategory {...articleCategoryStyles}>{category}</ArticleCategory>
      )}
      <ArticleTitle
        {...articleTitleStyles}
        color={articleTitleColor as Color}
        mb={isArticleInfoHidden ? 'sp_0' : ['sp_4', 'sp_8']}
      >
        {articleTitle}
      </ArticleTitle>
      {!isArticleInfoHidden && (
        <BodyBoldBB2>
          {authors}
          {publishingDate && <span>{`${articlePublishingDate} \u2022 `}</span>}
          <span>
            {readingTimeMinutes} min {t('h24_reading_time')}
          </span>
        </BodyBoldBB2>
      )}
    </ArticleHeaderContainer>
  );
};

export default ContentArticleHeader;
